/**
 * Amélioration n°8 (maquette) : barre sticky produit unifiée.
 * - Apparaît uniquement quand le bouton principal est sorti de l'écran par le HAUT
 *   (IntersectionObserver — plus de seuil scrollY figé).
 * - Prix : miroir du récapitulatif [data-sum-total] (déjà remisé et auto-réparé par
 *   le script promo) + data-gv-base pour le barré ; repli sur price:updated
 *   (fiches tubes/coffrets sans récapitulatif). Format français « X,XX € ».
 * - Variante : libellé de la carte .gv-v-card.selected (repli : select[name=id]).
 * - Ajout panier : délègue au bouton principal → une seule logique d'ajout.
 * - Voir les options : scroll doux vers le sélecteur de format + halo (repris de
 *   l'ancienne sticky-bar-enhanced du live, intégré ici).
 */

class ProductStickyBar extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.refresh = this.refresh.bind(this);
    this.onAddClick = this.onAddClick.bind(this);
    this.onPriceEvent = this.onPriceEvent.bind(this);
    this.onOptionsClick = this.onOptionsClick.bind(this);
  }

  connectedCallback() {
    this.addBtn = this.querySelector("[data-gv-sb-add]");
    this.optionsBtn = this.querySelector("[data-gv-sb-options]");
    if (this.optionsBtn) this.optionsBtn.addEventListener("click", this.onOptionsClick);
    this.elOld = this.querySelector("[data-gv-sb-old]");
    this.elNew = this.querySelector("[data-gv-sb-new]");
    this.elVariant = this.querySelector("[data-gv-sb-variant]");

    this.mainBtn =
      document.querySelector(".add-to-cart-row [data-gv-cart-btn]") ||
      document.querySelector("[data-gv-cart-btn]") ||
      document.querySelector("[data-add-to-cart]") ||
      document.querySelector(".main-product-cart-add");

    if (this.addBtn) this.addBtn.addEventListener("click", this.onAddClick);

    // Visibilité : le bouton principal doit être sorti par le haut de l'écran
    if (this.mainBtn && "IntersectionObserver" in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          const passed = !e.isIntersecting && e.boundingClientRect.bottom < 0;
          this.toggle(passed);
        },
        { threshold: 0 }
      );
      this.io.observe(this.mainBtn);
    }
    // Ceinture + bretelles : le scanner du thème peut affamer IO ET requestAnimationFrame
    // (constaté : seuls setTimeout/setInterval restent fiables) → throttle par setTimeout
    // au scroll + vérification périodique.
    const check = () => {
      if (this.mainBtn) {
        const r = this.mainBtn.getBoundingClientRect();
        this.toggle(r.bottom < 0);
      } else {
        this.toggle(window.scrollY > 1200);
      }
    };
    this.onScroll = () => {
      if (this._st) return;
      this._st = setTimeout(() => {
        this._st = null;
        check();
      }, 80);
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this._poll = setInterval(check, 600);

    // Prix : miroir du récapitulatif remisé quand il existe
    this.sumTotal = document.querySelector("[data-sum-total]");
    if (this.sumTotal) {
      this.mo = new MutationObserver(() => {
        clearTimeout(this._t);
        this._t = setTimeout(this.refresh, 120);
      });
      this.mo.observe(this.sumTotal, { childList: true, characterData: true, subtree: true, attributes: true });
      // le récap vit dans [data-variant-summary] — observer aussi la sélection de carte
      const root = document.querySelector("[data-gv-cards-root]");
      if (root) this.mo.observe(root, { attributes: true, subtree: true, attributeFilter: ["class"] });
      this.refresh();
      setTimeout(this.refresh, 800);
      setTimeout(this.refresh, 2500);
    }
    // Repli universel (tubes, coffrets, produits sans volet)
    document.addEventListener("price:updated", this.onPriceEvent);
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.mo) this.mo.disconnect();
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll);
    if (this._poll) clearInterval(this._poll);
    document.removeEventListener("price:updated", this.onPriceEvent);
  }

  toggle(show) {
    if (show === this.isVisible) return;
    this.isVisible = show;
    this.classList.toggle("is-visible", show);
    // Le scanner gèle les transitions CSS à t=0 → l'état final est forcé en inline
    // !important (la classe reste posée pour les navigateurs sains).
    this.style.setProperty("transition", "none", "important");
    this.style.setProperty("transform", show ? "none" : "translateY(110%)", "important");
  }

  fmt(cents) {
    return (cents / 100).toFixed(2).replace(".", ",") + " €";
  }

  parseCents(txt) {
    const m = (txt || "").match(/(\d+),(\d{2})/);
    return m ? parseInt(m[1], 10) * 100 + parseInt(m[2], 10) : 0;
  }

  refresh() {
    if (!this.sumTotal) return;
    const total = this.parseCents(this.sumTotal.textContent);
    const base = parseInt(this.sumTotal.getAttribute("data-gv-base") || "0", 10);
    if (total > 0 && this.elNew) {
      const newTxt = this.fmt(total);
      if (this.elNew.textContent !== newTxt) this.elNew.textContent = newTxt;
    }
    if (this.elOld) {
      if (base > total && total > 0) {
        const oldTxt = this.fmt(base);
        if (this.elOld.textContent !== oldTxt) this.elOld.textContent = oldTxt;
        this.elOld.hidden = false;
      } else {
        this.elOld.hidden = true;
      }
    }
    this.refreshVariant();
  }

  refreshVariant() {
    if (!this.elVariant) return;
    let label = "";
    const card = document.querySelector("[data-gv-cards-root] .gv-v-card.selected");
    if (card) {
      label = card.getAttribute("data-label") || "";
      const small = card.querySelector(".gv-v-card-qty small");
      if (small && small.textContent.trim()) label += " " + small.textContent.trim();
    } else {
      const sel = document.querySelector("select[name='id'][data-gv-variant-select], select[name='id']");
      if (sel && sel.selectedIndex >= 0) {
        label = (sel.options[sel.selectedIndex].textContent || "").split("|")[0].replace(/[«»]/g, "").trim();
      }
    }
    label = (label || "").trim();
    if (label && this.elVariant.textContent.trim() !== label) this.elVariant.textContent = label;
  }

  onPriceEvent(e) {
    // Fiches sans récapitulatif (tubes, coffrets) : source = price:updated (centimes)
    if (this.sumTotal) return;
    const d = (e && e.detail) || {};
    if (typeof d.totalPrice === "number" && this.elNew) {
      this.elNew.textContent = this.fmt(d.totalPrice);
      if (this.elOld) {
        if (typeof d.totalComparePrice === "number" && d.totalComparePrice > d.totalPrice) {
          this.elOld.textContent = this.fmt(d.totalComparePrice);
          this.elOld.hidden = false;
        } else {
          this.elOld.hidden = true;
        }
      }
    }
    this.refreshVariant();
  }

  onAddClick() {
    const target =
      document.querySelector(".add-to-cart-row [data-gv-cart-btn]") ||
      document.querySelector("[data-gv-cart-btn]") ||
      document.querySelector("[data-add-to-cart]") ||
      document.querySelector(".main-product-cart-add");
    if (target) target.click();
  }

  onOptionsClick() {
    // « Voir les options » ouvre le MÊME volet que le bouton CHOISIR des collections :
    // le pc-panel du produit est embarqué dans la barre (gv-variant-panel) et
    // GV.toggle suit exactement le même chemin de code.
    const pid = parseInt(this.optionsBtn.getAttribute("data-gv-sb-pid"), 10);
    const panel = this.optionsBtn.parentElement.querySelector(".pc-panel");
    if (window.GV && GV.toggle && panel && GV.products && GV.products[pid]) {
      GV.toggle(pid, this.optionsBtn);
      return;
    }
    // Repli (données volet indisponibles) : défilement vers le sélecteur de format
    const target =
      document.querySelector("[data-gv-cards-root]") ||
      document.querySelector("#ProductForm, .product-form, product-form") ||
      document.querySelector(".main-product__section--right");
    if (!target) {
      this.animateScroll(0);
      return;
    }
    const r = target.getBoundingClientRect();
    const dest = Math.max(0, window.scrollY + r.top - Math.max(20, (window.innerHeight - r.height) / 2));
    this.animateScroll(dest);
    setTimeout(() => {
      target.classList.add("gv-highlight-target");
      setTimeout(() => target.classList.remove("gv-highlight-target"), 1500);
    }, 650);
  }

  // Le scanner du thème gèle le smooth-scroll natif (et rAF) → défilement animé
  // par setInterval, seul mécanisme fiable ici.
  animateScroll(dest) {
    if (this._anim) clearInterval(this._anim);
    const depart = window.scrollY;
    const dist = dest - depart;
    if (Math.abs(dist) < 2) return;
    const duree = 500;
    const t0 = Date.now();
    this._anim = setInterval(() => {
      const p = Math.min(1, (Date.now() - t0) / duree);
      const e = 1 - Math.pow(1 - p, 3);
      window.scrollTo(0, depart + dist * e);
      if (p >= 1) {
        clearInterval(this._anim);
        this._anim = null;
      }
    }, 16);
  }
}

customElements.define("product-sticky-bar", ProductStickyBar);
